import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import { BaseEntityType } from "../../types";
// ✅ PHASE 2: HierarchyStore removed - filtering moved to EntityStore
import { useDataspaceTreeStore } from "../../stores/dataspaceTreeStore";
import { useSpaceStore } from "../../stores/spaceStore";
import { resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('SPACE_GET_CONTEXT');

export const spaceGetContextCommand: SelfContainedCommand = {
  id: 'space_get_context',
  description: `Get comprehensive information about a Space - its entities, organization, and current workspace state.

**Primary Use: Workspace Discovery**
CRITICAL: Call this tool FIRST when the user asks about workspace contents, available files, or what entities exist. The context you receive only shows files explicitly added - it does NOT automatically include all workspace entities.

**When to Use:**
- User asks "what files/documents do I have?" → Call space_get_context() FIRST
- User asks "show me my workspace" → Call space_get_context() FIRST  
- User asks "what can I work with?" → Call space_get_context() FIRST
- ⭐ **User mentions an API (e.g., "use our Mixpanel API")** → Call space_get_context() to find APIConnection entities!
- Before making assumptions about what exists in the workspace
- To see recently modified content
- To understand Space organization before taking action

**What You'll Get:**
- Complete list of all entities in this Space (documents, notes, folders, etc.)
- **APIConnection and Integration entities** (org-level shared resources accessible from any space)
- Entity type breakdown (how many of each type)
- Recently modified items
- Workspace state (open windows, if includeWorkspace=true)

**IMPORTANT FOR API WORKFLOWS:**
APIConnection and Integration entities are org-level shared resources (spaceId=null) that are accessible from any space. If you see APIConnection entities in the results, you can read them to get credentials and configuration for external APIs. This is essential for compute sessions that need API access!`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID to inspect. If not provided, uses the current active Space from context. Space IDs follow the pattern: space_{adj}_{noun}_{adj}_{noun}.',
        required: false,
        defaultValue: 'current',
        examples: [
          'space_bright_ocean_calm_river',
          'space_wise_mountain_soft_breeze',
          'space_happy_garden_bright_dawn'
        ],
        validator: (value: any) => {
          // null/undefined is valid (means current space)
          if (value === null || value === undefined) return null;
          
          // Must be a string
          if (typeof value !== 'string') return 'Space ID must be a string';
          
          // Cannot be empty string
          if (value.trim().length === 0) return 'Space ID cannot be empty string';
          
          return null;
        }
      },
      {
        name: 'includeWorkspace',
        type: 'boolean',
        description: 'Whether to include current workspace state (open windows, chat visibility, etc). Defaults to false since entity discovery is usually the primary goal.',
        required: false,
        defaultValue: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {},
        expectedOutput: createSuccessResult('Space context retrieved', {
          spaceId: 'space_bright_ocean_calm_river',
          spaceName: 'My Workspace',
          contents: {
            totalEntities: 15,
            rootEntities: 8,
            entityTypes: {
              Doc: 5,
              Group: 2,
              Note: 3,
              Thread: 2,
              StoryCharacter: 3
            },
            recentlyModified: [
              { id: 'doc_green_tree', name: 'Project Plan.docx', entityType: 'Doc', updated_at: '2024-01-15T10:30:00Z' },
              { id: 'note_red_apple', name: 'Meeting Notes', entityType: 'Note', updated_at: '2024-01-15T09:15:00Z' }
            ],
            entities: [
              { id: 'doc_green_tree', name: 'Project Plan.docx', entityType: 'Doc' },
              { id: 'group_blue_sky', name: 'Research', entityType: 'Group', childCount: 5 },
              { id: 'note_red_apple', name: 'Meeting Notes', entityType: 'Note' }
            ]
          }
        }),
        description: 'Get context for current Space (entity discovery focus)',
        context: 'When you need to see what entities exist in the current Space before taking action'
      },
      {
        input: { spaceId: 'space_wise_mountain', includeWorkspace: true },
        expectedOutput: createSuccessResult('Space context retrieved', {
          spaceId: 'space_wise_mountain',
          spaceName: 'Writing Project',
          contents: {
            totalEntities: 12,
            rootEntities: 6,
            entityTypes: { Doc: 8, Group: 3, StoryCharacter: 1 },
            recentlyModified: [],
            entities: []
          },
          workspace: {
            windowsOpen: 3,
            windows: [
              { id: 'window-123', title: 'Chapter 1.docx', appType: 'docs', entityId: 'doc_abc', isFocused: true },
              { id: 'window-456', title: 'Character Sheet', appType: 'storycharacter', entityId: 'char_xyz', isFocused: false }
            ],
            chatVisible: true,
            sidebarVisible: true,
            viewMode: 'focused'
          }
        }),
        description: 'Get full context including workspace state',
        context: 'When you need both entity discovery AND current workspace state'
      }
    ],
    bad: [
      {
        input: { spaceId: '' },
        error: 'Space ID cannot be empty string',
        why: 'Empty string is invalid',
        fix: 'Omit the parameter to use current Space, or provide a valid Space ID'
      },
      {
        input: { spaceId: 'nonexistent_space' },
        error: 'Space not found',
        why: 'The Space ID does not exist',
        fix: 'Use entity_find with entityType=Space to discover valid Space IDs, or omit spaceId to use current Space'
      }
    ]
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  formatViewData: (result: any, _input: any) => {
    if (!result || result.status !== 'success') {
      return undefined;
    }
    
    const data = result.data || {};
    
    return {
      type: 'space_context',
      summary: result.message || `Space: ${data.spaceName || data.spaceId}`,
      data: {
        spaceName: data.spaceName,
        totalEntities: data.contents?.totalEntities || 0,
        entityTypes: data.contents?.entityTypes || {},
        windowsOpen: data.workspace?.windowsOpen || 0
      }
    };
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { spaceId, includeWorkspace = false } = params;
    
    try {
      // If no spaceId provided, use current active space
      if (!spaceId || spaceId === 'current') {
        const spaceStore = useSpaceStore.getState();
        const activeSpace = spaceStore.activeSpace;
        
        if (!activeSpace) {
          return createErrorResult(
            'No active Space',
            'No Space ID provided and no active Space in context. Please provide a spaceId or ensure you are in a Space.',
            'validation'
          );
        }
        
        spaceId = activeSpace.id;
        log.info('[space_get_context] Using active Space:', spaceId);
      } else {
        // Resolve llmId to _id if needed
        const resolution = resolveEntityId(spaceId);
        spaceId = resolution.resolved;
        
        log.info('[space_get_context] Resolved Space ID:', { 
          original: params.spaceId, 
          resolved: spaceId,
          isLlmId: resolution.isLlmId 
        });
      }
      
      const entityStore = useEntityStore.getState();
      // Get displayable entities (filtered by type)
      const allEntitiesProcessed = entityStore.getDisplayableEntities();
      
      // Get the Space entity
      const spaceEntity = entityStore.entities.find((e: BaseEntityType) => e._id === spaceId && e.entityType === 'Space');
      
      if (!spaceEntity) {
        return createErrorResult(
          'Space not found',
          `Space with ID ${spaceId} does not exist`,
          'validation',
          { data: { spaceId } }
        );
      }
      
      const spaceName = spaceEntity.name || 'Untitled Space';
      
      console.log('[space_get_context] 🔍 Filtering entities for space:', JSON.stringify({
        spaceId,
        spaceName,
        totalEntitiesInStore: entityStore.entities.length,
        currentSpaceId: entityStore.currentSpaceId
      }, null, 2));
      
      // Get all entities that belong to this Space
      // ✅ CRITICAL: Also include org-level shared resources (APIConnection, Integration)
      const spaceEntities = entityStore.entities.filter((e: BaseEntityType) => {
        // Filter by spaceId and exclude system types
        const belongsToSpace = (e as any).spaceId === spaceId;
        
        // ⭐ SHARED RESOURCES: Include org-level APIConnections and Integrations
        // These have spaceId=null but should be accessible from any space
        const isSharedResource = (
          (e.entityType === 'APIConnection' || e.entityType === 'Integration') &&
          ((e as any).spaceId === null || (e as any).spaceId === undefined)
        );
        
        const isSystemType = ['DocVersion', 'Message', 'Space'].includes(e.entityType);
        const isHidden = (e as any).trashed === true || (e as any).hidden === true;
        
        return (belongsToSpace || isSharedResource) && !isSystemType && !isHidden;
      });
      
      console.log('[space_get_context] 📊 Found entities in Space:', JSON.stringify({
        spaceId,
        count: spaceEntities.length,
        includesSharedResources: spaceEntities.some(e => 
          (e.entityType === 'APIConnection' || e.entityType === 'Integration') &&
          ((e as any).spaceId === null || (e as any).spaceId === undefined)
        ),
        entityTypes: Array.from(new Set(spaceEntities.map(e => e.entityType)))
      }, null, 2));
      
      // Calculate entity type breakdown
      const entityTypes: Record<string, number> = {};
      spaceEntities.forEach((e: BaseEntityType) => {
        entityTypes[e.entityType] = (entityTypes[e.entityType] || 0) + 1;
      });
      
      // Get root-level entities (not in any folder)
      const rootEntities = spaceEntities.filter((e: BaseEntityType) => {
        const groupIds = (e.skeleton as any)?.groupIds || [];
        return groupIds.length === 0;
      });
      
      // Get recently modified entities (last 10)
      const recentlyModified = [...spaceEntities]
        .sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
          const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
          return dateB - dateA;
        })
        .slice(0, 10)
        .map(e => ({
          id: e._id,
          name: e.name,
          entityType: e.entityType,
          updated_at: e.updated_at,
          created_at: e.created_at
        }));
      
      // Build entity list with useful metadata
      const entities = spaceEntities.map((e: BaseEntityType) => {
        const baseInfo = {
          id: e._id,
          name: e.name,
          entityType: e.entityType,
          created_at: e.created_at,
          updated_at: e.updated_at
        };
        
        // Add childCount for Groups
        if (e.entityType === 'Group') {
          const { getChildNodeIds } = useDataspaceTreeStore.getState();
          return {
            ...baseInfo,
            childCount: getChildNodeIds(e._id).length
          };
        }
        
        return baseInfo;
      });
      
      // Build result with contents as primary focus
      const result: any = {
        spaceId,
        spaceName,
        contents: {
          totalEntities: spaceEntities.length,
          rootEntities: rootEntities.length,
          entityTypes,
          recentlyModified,
          entities
        }
      };
      
      // Optionally include workspace state
      if (includeWorkspace) {
        const spaceStore = useSpaceStore.getState();
        const windows = spaceStore.windows;
        const activeWindowId = spaceStore.activeWindowId;
        
        // Only include windows if we're looking at the active space
        if (spaceStore.activeSpace?.id === spaceId && windows) {
          const windowList = Object.values(windows)
            .filter(w => w.type === 'window' && w.appType !== 'group')
            .map(w => ({
              id: w.id,
              title: w.title || 'Untitled',
              appType: w.appType || 'unknown',
              entityId: (w as any).entityId,
              entityName: entities.find((e: any) => e.id === (w as any).entityId)?.name,
              isFocused: w.id === activeWindowId
            }));
          
          result.workspace = {
            windowsOpen: windowList.length,
            windows: windowList,
            chatVisible: spaceStore.isChatVisible || false,
            sidebarVisible: spaceStore.isSidebarVisible || false,
            viewMode: spaceStore.activeSpace?.settings?.viewMode || 'focused'
          };
          
          log.info('[space_get_context] Included workspace state:', {
            windowsOpen: windowList.length,
            viewMode: result.workspace.viewMode
          });
        }
      }
      
      return createSuccessResult(
        `Found ${spaceEntities.length} entities in Space "${spaceName}"`,
        result,
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            entityCount: spaceEntities.length,
            includesWorkspace: includeWorkspace
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[space_get_context] Error:', error);
      return createErrorResult(
        'Failed to get Space context',
        error.message || 'Unknown error',
        'execution',
        {
          data: { spaceId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};



